<!--
  A convenient editor for all examples as a single textfile

  Props:
    examples: the array of all examples

  Emits:
    save(examples): the new examples
    close: to close the editor
-->

<template>
  <el-form class="examples-text-editor"
    ref="form"
    :model="{examples: text}"
    label-width="150px"
    :hide-required-asterisk="true">
    <el-row>
      <!-- the main textarea to edit the examples -->
      <el-col :span="24" style="margin-bottom: 20px;">
        <el-form-small-item
          prop="examples"
          :rules="examplesValidator">
          <el-input type="textarea" :rows="20"
            v-model="text"></el-input>
        </el-form-small-item>
      </el-col>
      <!-- Cancel / Reset / Validate buttons -->
      <el-col :span="24" style="text-align: center">
        <el-button-group style="justify-content: center;">
          <el-button @click="handleCancel" type="danger">Cancel</el-button>
          <el-button @click="handleReset" type="warning">Reset</el-button>
          <el-button @click="handleSave" type="primary">Validate</el-button>
        </el-button-group>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
import {
  VARIBALE_REGEX, SLOT_TEXT_REGEX, EXAMPLE_REGEX,
} from '../../utils/validators'
import ElFormSmallItem from '../element/ElFormSmallItem.vue'

export default {
  name: 'ExamplesTextEditor',
  components: {
    ElFormSmallItem,
  },
  props: {
    examples: Array,
  },
  computed: {
    /* the text to display in the textarea */
    examplesText () { return this.examples.join('\n') + '\n' },
  },
  data () {
    let self = this
    return {
      text: undefined, // the text to display in the textarea
      outputs: null, // the array of inputs to save
      errors: [], // the errors to display
      examplesValidator: { // the validator for the textarea
        validator (rule, value, callback) {
          callback(
            self.errors.length ? new Error(self.errors[0]) : undefined
          )
        },
        trigger: 'blur',
      },
    }
  },
  /* init the model */
  created () { this.text = this.examplesText },
  watch: {
    /* update the ouput when the model changed */
    'text' () { this.examplesChanged() },
  },
  methods: {
    /* update the ouput and synonymsRef */
    examplesChanged () {
      let outputs = [], errors = []
      // check in each line which synonym is used, and errors
      const reg = /\{@([0-9a-zA-Z_]*):([^}]+)\}/g
      this.text.split('\n').forEach((example, line) => {
        example = example.replace(/^\s+|\s+$/g, '')
        if (!example) return
        let error = null, seen = {}
        example.replace(reg, (_, slot, stext) => {
          if (!error) {
            if (!slot.match(VARIBALE_REGEX)) {
              error = 'invalid slot name ' + JSON.stringify(slot)
            } else if (!stext.match(SLOT_TEXT_REGEX)) {
              error = 'invalid slot text ' + JSON.stringify(stext)
            } else if (seen[slot]) {
              error = 'duplicate slot ' + JSON.stringify(slot)
            }
          }
          seen[slot] = true
          return ''
        })
        if (!error && !example.match(EXAMPLE_REGEX)) {
          error = 'invalid example'
        }
        if (error) {
          errors.push('line ' + (line + 1) + ': ' + error)
        } else {
          outputs.push(example)
        }
      })
      this.outputs = outputs
      this.errors = errors
    },
    /* closes the editor */
    handleCancel () {
      if (this.examplesText === this.text) {
        this.$emit('close')
        return
      }
      this.$confirm('This will cancel the examples edition', 'Cancel', {
        type: 'warning',
      }).then(() => {
        this.$emit('close')
      }).catch(() => {})
    },
    /* resets the editor with inputs */
    handleReset () {
      if (this.examplesText === this.text) {
        return
      }
      this.$confirm('This will reset the examples edition', 'Reset', {
        type: 'warning',
      }).then(() => {
        this.text = this.examplesText
      }).catch(() => {})
    },
    /* save the inputs */
    handleSave () {
      if (this.examplesText === this.text) {
        this.$emit('close')
        return
      }
      this.$refs.form.validate(valid => {
        if (valid) {
          this.$confirm('This will validate the examples', 'Validate', {
            type: 'success',
          }).then(() => {
            this.$emit('save', this.outputs)
          }).catch(() => {})
        } else {
          this.$message.error('Please correct errors')
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import "../../element-variables.scss";

.inputs-text-editor textarea {
  line-height: 150%;
  white-space: pre;
}
</style>
